import React, { useState, useCallback } from "react";
import InputField from "../components/ui/InputField";
import SearchSelect from "../components/ui/SearchSelect";
import SuccessPopup from "../components/ui/PopUp";
import RadioButton from "../components/ui/RadioButton";
import Button from "../components/ui/Button";
import Logo from "../assets/images/churchLogo.png";
import styles from "../styles/MembershipForm.module.css";

// Constants
const DEPARTMENT_TYPES = ["Light of Worship", "Abarinzi b'Inkike", "IT", "Media", "Ushers", "Decoration", "Evangelism", "Welfare"];
const MARITAL_STATUS = ["Single", "Married", "Divorced", "Widowed", "Separated"];
const RELATIONSHIP_OPTIONS = ["Spouse", "Son", "Daughter", "Father", "Mother", "Brother", "Sister", "Guardian", "Other"];

const CHURCH_DATA = {
  "Kigali Region": ["Kigali Central Parish", "Kimironko Parish", "Remera Parish", "Nyamirambo Parish"],
  "Northern Rwanda Region": ["Musanze Parish", "Ruhengeri Parish"],
  "Nairobi Region": ["Nairobi Central Parish", "Westlands Parish", "Kibera Parish"],
  "Mombasa Region": ["Mombasa Parish"],
  "Kampala Region": ["Kampala Parish", "Entebbe Parish", "Makindye Parish"],
  "Western Uganda Region": ["Mbarara Parish", "Fort Portal Parish"],
  "Kinshasa Region": ["Kinshasa Parish", "Gombe Parish"],
  "Goma Region": ["Goma Parish"],
  "London Region": ["London Parish"],
  "Berlin Region": ["Berlin Parish"],
};

const LOCATION_DATA = {
  Africa: {
    Rwanda: {
      "Kigali City": {
        "Gasabo District": {
          "Remera Sector": {
            "Rukiri I Cell": ["Rukiri Village", "Nyarutarama Village"],
            "Rukiri II Cell": ["Kimihurura Village"],
          },
          "Kimironko Sector": {
            "Kibagabaga Cell": ["Kibagabaga Village"],
            "Nyabisindu Cell": ["Nyabisindu Village"],
          },
        },
        "Kicukiro District": {
          "Gikondo Sector": {
            "Gikondo Cell": ["Gikondo Village", "Nyanza Village"],
          },
          "Niboye Sector": {
            "Niboye Cell": ["Niboye Village"],
          },
        },
      },
      "Northern Province": {
        "Musanze District": {
          "Muhoza Sector": {
            "Muhoza Cell": ["Muhoza Village"],
          },
        },
      },
    },
    Kenya: {
      "Nairobi County": {
        "Nairobi District": {
          "Westlands Sector": {
            "Parklands Cell": ["Parklands Village"],
          },
          "Kibera Sector": {
            "Kibera Cell": ["Kibera Village"],
          },
        },
      },
    },
    Uganda: {
      "Central Region": {
        "Kampala District": {
          "Central Sector": {
            "Kampala Cell": ["Kampala Village"],
          },
        },
      },
    },
    "DR Congo": {
      "Kinshasa Province": {
        "Kinshasa District": {
          "Gombe Sector": {
            "Gombe Cell": ["Gombe Village"],
          },
        },
      },
    },
  },
  Europe: {
    "United Kingdom": {
      "England": {
        "Greater London": {
          "Westminster Sector": {
            "Westminster Cell": ["Westminster Village"],
          },
        },
      },
    },
    Germany: {
      "Berlin State": {
        "Berlin District": {
          "Mitte Sector": {
            "Mitte Cell": ["Mitte Village"],
          },
        },
      },
    },
  },
};

// Step configurations - removed icons from titles
const STEP_CONFIG = {
  1: { title: "Personal Information", subtitle: "Tell us about yourself" },
  2: { title: "Church Location", subtitle: "Where will you worship?" },
  3: { title: "Residential Address", subtitle: "Where do you live?" },
  4: { title: "Church Information", subtitle: "Tell us about your spiritual journey" },
  5: { title: "Review Your Application", subtitle: "Please review your information before submitting" },
};

// Initial form state
const INITIAL_FORM_DATA = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  gender: "",
  dateOfBirth: "",
  maritalStatus: "",
  idCard: "",
  region: "",
  parish: "",
  addressContinent: "",
  addressCountry: "",
  addressProvince: "",
  addressDistrict: "",
  addressSector: "",
  addressCell: "",
  addressVillage: "",
  street: "",
  postalCode: "",
  isBaptized: "",
  baptismDate: "",
  interestedMinistries: [],
  joiningWithFamily: "",
  familyMembers: [],
};

const INITIAL_FAMILY_MEMBER = {
  firstName: "",
  lastName: "",
  relationship: "",
  dateOfBirth: "",
  gender: "",
  idCard: "",
};

// Bible verses for welcome message
const BIBLE_VERSES = [
  "For where two or three gather in my name, there am I with them. - Matthew 18:20",
  "Welcome one another, therefore, just as Christ has welcomed you, for the glory of God. - Romans 15:7",
  "Let us not give up meeting together, as some are in the habit of doing, but let us encourage one another. - Hebrews 10:25",
  "How good and pleasant it is when God's people live together in unity! - Psalm 133:1",
  "Come to me, all you who are weary and burdened, and I will give you rest. - Matthew 11:28"
];

// Progress Indicator Component
const CustomProgressIndicator = ({ currentStep, totalSteps, onStepClick }) => {
  return (
    <div className={styles["progress-container"]}>
      <div 
        className={styles["progress-bar"]} 
        style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
      ></div>
      {[...Array(totalSteps)].map((_, index) => {
        const stepNumber = index + 1;
        const isCompleted = stepNumber < currentStep;
        const isActive = stepNumber === currentStep;
        
        return (
          <div
            key={stepNumber}
            className={`${styles["progress-step"]} ${
              isActive ? styles["active"] : ""
            } ${isCompleted ? styles["completed"] : ""}`}
            onClick={() => onStepClick(stepNumber)}
          >
            <div className={styles["step-number"]}>
              {isCompleted ? "✓" : stepNumber}
            </div>
            <div className={styles["step-label"]}>
              {STEP_CONFIG[stepNumber]?.title.split(" ")[0]}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// InfoBox Component
const InfoBox = ({ icon, children, styles }) => (
  <div className={styles["info-box"]}>
    <span className={styles["info-icon"]}>{icon}</span>
    <div className={styles["info-content"]}>
      {children}
    </div>
  </div>
);

// FamilyMembersList Component
const FamilyMembersList = ({ familyMembers, onShowFamilyModal, onRemoveFamilyMember, calculateAge, styles }) => (
  <div className={styles["family-list-container"]}>
    <div className={styles["family-list-header"]}>
      <div className={styles["family-title"]}>
        <strong>Family Members ({familyMembers.length})</strong>
      </div>
      <button 
        className={styles["add-family-button"]}
        onClick={onShowFamilyModal}
        type="button"
      >
        <span className={styles["button-icon"]}>+</span>
        Add Family Members
      </button>
    </div>

    {familyMembers.length > 0 ? (
      <div className={styles["family-list"]}>
        {familyMembers.map(member => {
          const memberAge = calculateAge(member.dateOfBirth);
          const requiresId = memberAge >= 16;
          return (
            <div key={member.id} className={styles["family-member-card"]}>
              <div className={styles["family-member-info"]}>
                <div className={styles["member-name"]}>
                  <strong>{member.firstName} {member.lastName}</strong>
                  {requiresId && member.idCard && (
                    <span className={styles["id-verified"]}>🆔 Verified</span>
                  )}
                </div>
                <span className={styles["family-member-detail"]}>
                  {member.relationship} • {member.gender} • Age {memberAge}
                  {requiresId && !member.idCard && (
                    <span className={styles["id-required"]}> • ID Required</span>
                  )}
                </span>
              </div>
              <button
                type="button"
                onClick={() => onRemoveFamilyMember(member.id)}
                className={styles["remove-button"]}
                aria-label={`Remove ${member.firstName} ${member.lastName}`}
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>
    ) : (
      <div className={styles["empty-family-state"]}>
        <p>No family members added yet</p>
        <p className={styles["empty-subtext"]}>Click "Add Family Members" to start</p>
      </div>
    )}
  </div>
);

// ReviewSection Component - removed icons
const ReviewSection = ({ title, items, styles, ReviewItem }) => (
  <div className={styles["review-section"]}>
    <h3 className={styles["review-section-title"]}>
      {title}
    </h3>
    <div className={styles["review-items"]}>
      {items.map((item, index) => (
        <ReviewItem key={index} label={item.label} value={item.value} />
      ))}
    </div>
  </div>
);

// PersonalInfoStep Component
const PersonalInfoStep = ({ formData, updateFormData, errors, currentAge, requiresIdCard, onJoiningWithFamilyChange, onShowFamilyModal, onRemoveFamilyMember, calculateAge, styles }) => (
  <div className={styles["step-content"]}>
    <InfoBox icon="📍" styles={styles}>
      <p className={styles["info-text"]}>
        Please provide your personal details accurately. This information helps us create your membership profile.
      </p>
    </InfoBox>

    <div className={styles["form-row"]}>
      <div className={styles["form-col"]}>
        <InputField
          label="First Name"
          placeholder="Enter your first name"
          value={formData.firstName}
          onChange={(e) => updateFormData({ firstName: e.target.value })}
          error={errors.firstName}
          required
        />
      </div>
      <div className={styles["form-col"]}>
        <InputField
          label="Last Name"
          placeholder="Enter your last name"
          value={formData.lastName}
          onChange={(e) => updateFormData({ lastName: e.target.value })}
          error={errors.lastName}
          required
        />
      </div>
    </div>

    <div className={styles["form-row"]}>
      <div className={styles["form-col"]}>
        <InputField
          type="email"
          label="Email Address"
          placeholder="your.email@example.com"
          value={formData.email}
          onChange={(e) => updateFormData({ email: e.target.value })}
          error={errors.email}
          required
        />
      </div>
      <div className={styles["form-col"]}>
        <InputField
          type="tel"
          label="Phone Number"
          placeholder="+250 788 123 456"
          value={formData.phone}
          onChange={(e) => updateFormData({ phone: e.target.value })}
          error={errors.phone}
          required
        />
      </div>
    </div>

    <InputField
      type="date"
      label="Date of Birth"
      value={formData.dateOfBirth}
      onChange={(e) => updateFormData({ dateOfBirth: e.target.value })}
      error={errors.dateOfBirth}
      required
    />
    
    {formData.dateOfBirth && (
      <div className={`${styles["age-display"]} ${requiresIdCard ? styles["id-required"] : ""}`}>
        <span>Age: {currentAge} years old</span>
        {requiresIdCard && <span className={styles["id-badge"]}> • ID Card Required</span>}
      </div>
    )}

    {requiresIdCard && (
      <>
        <InputField
          label="Identification Card Number"
          placeholder="Enter your ID card number"
          value={formData.idCard}
          onChange={(e) => updateFormData({ idCard: e.target.value })}
          error={errors.idCard}
          required
        />
        <p className={styles["help-text"]}>
          Required for applicants 16 years and above for verification purposes
        </p>
      </>
    )}

    <RadioButton
      label="Gender"
      options={["Male", "Female"]}
      value={formData.gender}
      onChange={(value) => updateFormData({ gender: value })}
      error={errors.gender}
      required
    />

    <SearchSelect
      label="Marital Status"
      value={formData.maritalStatus}
      onChange={(value) => updateFormData({ maritalStatus: value })}
      options={MARITAL_STATUS}
      placeholder="-- Select Marital Status --"
      error={errors.maritalStatus}
      required
    />

    {formData.maritalStatus === "Married" && (
      <InputField
        type="date"
        label="Marriage Date"
        placeholder="Select marriage date"
        value={formData.marriageDate || ""}
        onChange={(e) => updateFormData({ marriageDate: e.target.value })}
        error={errors.marriageDate}
      />
    )}

    {formData.maritalStatus && (
      <RadioButton
        label="Are you joining with family members?"
        options={["Yes", "No"]}
        value={formData.joiningWithFamily}
        onChange={onJoiningWithFamilyChange}
        required
      />
    )}

    {formData.joiningWithFamily === "Yes" && (
      <FamilyMembersList
        familyMembers={formData.familyMembers}
        onShowFamilyModal={onShowFamilyModal}
        onRemoveFamilyMember={onRemoveFamilyMember}
        calculateAge={calculateAge}
        styles={styles}
      />
    )}
  </div>
);

// ChurchLocationStep Component
const ChurchLocationStep = ({ formData, updateFormData, errors, styles }) => (
  <div className={styles["step-content"]}>
    <InfoBox icon="📍" styles={styles}>
      <p className={styles["info-text"]}>
        Select the region and parish where you plan to worship regularly. 
        This helps us connect you with the right church community.
      </p>
    </InfoBox>

    <div className={styles["form-row"]}>
      <div className={styles["form-col"]}>
        <SearchSelect
          label="Region"
          value={formData.region}
          onChange={(value) => updateFormData({ region: value, parish: "" })}
          options={Object.keys(CHURCH_DATA)}
          placeholder="-- Select Region --"
          error={errors.region}
          required
        />
      </div>
      <div className={styles["form-col"]}>
        <SearchSelect
          label="Parish"
          value={formData.parish}
          onChange={(value) => updateFormData({ parish: value })}
          options={formData.region ? CHURCH_DATA[formData.region] : []}
          placeholder={formData.region ? "-- Select Parish --" : "-- Select Region First --"}
          disabled={!formData.region}
          error={errors.parish}
          required
        />
      </div>
    </div>
  </div>
);

// AddressStep Component
const AddressStep = ({ formData, updateFormData, errors, locationOptions, styles }) => (
  <div className={styles["step-content"]}>
    <InfoBox icon="📍" styles={styles}>
      <p className={styles["info-text"]}>
        Provide your complete residential address for church records and communication
      </p>
    </InfoBox>

    <div className={styles["form-row"]}>
      <div className={styles["form-col"]}>
        <SearchSelect
          label="Continent"
          value={formData.addressContinent}
          onChange={(value) => updateFormData({ 
            addressContinent: value,
            addressCountry: "", addressProvince: "", addressDistrict: "",
            addressSector: "", addressCell: "", addressVillage: ""
          })}
          options={Object.keys(LOCATION_DATA)}
          placeholder="-- Select Continent --"
          error={errors.addressContinent}
          required
        />
      </div>
      <div className={styles["form-col"]}>
        <SearchSelect
          label="Country"
          value={formData.addressCountry}
          onChange={(value) => updateFormData({ 
            addressCountry: value,
            addressProvince: "", addressDistrict: "", addressSector: "",
            addressCell: "", addressVillage: ""
          })}
          options={locationOptions.countries}
          placeholder={formData.addressContinent ? "-- Select Country --" : "-- Select Continent First --"}
          disabled={!formData.addressContinent}
          error={errors.addressCountry}
          required
        />
      </div>
    </div>

    <div className={styles["form-row"]}>
      <div className={styles["form-col"]}>
        <SearchSelect
          label="Province/State"
          value={formData.addressProvince}
          onChange={(value) => updateFormData({ 
            addressProvince: value,
            addressDistrict: "", addressSector: "", addressCell: "", addressVillage: ""
          })}
          options={locationOptions.provinces}
          placeholder={formData.addressCountry ? "-- Select Province --" : "-- Select Country First --"}
          disabled={!formData.addressCountry}
          error={errors.addressProvince}
          required
        />
      </div>
      <div className={styles["form-col"]}>
        <SearchSelect
          label="District"
          value={formData.addressDistrict}
          onChange={(value) => updateFormData({ 
            addressDistrict: value,
            addressSector: "", addressCell: "", addressVillage: ""
          })}
          options={locationOptions.districts}
          placeholder={formData.addressProvince ? "-- Select District --" : "-- Select Province First --"}
          disabled={!formData.addressProvince}
          error={errors.addressDistrict}
          required
        />
      </div>
    </div>

    <div className={styles["form-row"]}>
      <div className={styles["form-col"]}>
        <SearchSelect
          label="Sector"
          value={formData.addressSector}
          onChange={(value) => updateFormData({ 
            addressSector: value,
            addressCell: "", addressVillage: ""
          })}
          options={locationOptions.sectors}
          placeholder={formData.addressDistrict ? "-- Select Sector --" : "-- Select District First --"}
          disabled={!formData.addressDistrict}
          error={errors.addressSector}
          required
        />
      </div>
      <div className={styles["form-col"]}>
        <SearchSelect
          label="Cell"
          value={formData.addressCell}
          onChange={(value) => updateFormData({ 
            addressCell: value,
            addressVillage: ""
          })}
          options={locationOptions.cells}
          placeholder={formData.addressSector ? "-- Select Cell --" : "-- Select Sector First --"}
          disabled={!formData.addressSector}
          error={errors.addressCell}
          required
        />
      </div>
    </div>

    <SearchSelect
      label="Village"
      value={formData.addressVillage}
      onChange={(value) => updateFormData({ addressVillage: value })}
      options={locationOptions.villages}
      placeholder={formData.addressCell ? "-- Select Village --" : "-- Select Cell First --"}
      disabled={!formData.addressCell}
      error={errors.addressVillage}
      required
    />

    <div className={styles["form-row"]}>
      <div className={styles["form-col"]}>
        <InputField
          label="Street Address"
          placeholder="Enter your street address"
          value={formData.street}
          onChange={(e) => updateFormData({ street: e.target.value })}
          error={errors.street}
          required
        />
      </div>
      <div className={styles["form-col"]}>
        <InputField
          label="Postal Code"
          placeholder="Postal code (optional)"
          value={formData.postalCode}
          onChange={(e) => updateFormData({ postalCode: e.target.value })}
          required={false}
        />
      </div>
    </div>
  </div>
);

// ChurchInfoStep Component
const ChurchInfoStep = ({ formData, updateFormData, errors, onBaptismChange, onMinistryChange, styles }) => (
  <div className={styles["step-content"]}>
    <InfoBox icon="📍" styles={styles}>
      <p className={styles["info-text"]}>
        Share your baptism information to help us understand your spiritual journey
      </p>
    </InfoBox>

    <RadioButton
      label="Are you baptized?"
      options={["Yes", "No"]}
      value={formData.isBaptized}
      onChange={onBaptismChange}
      error={errors.isBaptized}
      required
    />

    {formData.isBaptized === "Yes" && (
      <>
        <InputField
          type="date"
          label="Baptism Date"
          value={formData.baptismDate}
          onChange={(e) => updateFormData({ baptismDate: e.target.value })}
          error={errors.baptismDate}
          required
        />
        <p className={styles["help-text"]}>
          Please provide the date you were baptized
        </p>
      </>
    )}

    <div className={styles["field-group"]}>
      <label className={styles["field-label"]}>
        Interested Ministries (Optional)
      </label>
      <p className={styles["help-text"]}>
        Select ministries you'd like to serve in. You can always change this later.
      </p>
      <div className={styles["checkbox-grid"]}>
        {DEPARTMENT_TYPES.map(ministry => (
          <label key={ministry} className={styles["checkbox-label"]}>
            <input
              type="checkbox"
              checked={formData.interestedMinistries.includes(ministry)}
              onChange={() => onMinistryChange(ministry)}
              className={styles["checkbox-input"]}
            />
            <span className={styles["checkbox-custom"]}></span>
            <span className={styles["checkbox-text"]}>{ministry}</span>
          </label>
        ))}
      </div>
    </div>
  </div>
);

// ReviewStep Component - removed icons
const ReviewStep = ({ formData, currentAge, requiresIdCard, calculateAge, styles, ReviewItem }) => (
  <div className={styles["step-content"]}>
    <InfoBox icon="📍" styles={styles}>
      <p className={styles["info-text"]}>
        Please review your application carefully. Use the back button to make corrections if needed.
      </p>
    </InfoBox>

    <div className={styles["review-grid"]}>
      <ReviewSection 
        title="Personal Information"
        items={[
          { label: "Full Name", value: `${formData.firstName} ${formData.lastName}` },
          { label: "Email", value: formData.email },
          { label: "Phone", value: formData.phone },
          { label: "Gender", value: formData.gender },
          { label: "Date of Birth", value: `${formData.dateOfBirth} (Age: ${currentAge})` },
          ...(requiresIdCard ? [{ label: "ID Card", value: formData.idCard || "Not provided" }] : []),
          { label: "Marital Status", value: formData.maritalStatus },
          ...(formData.maritalStatus === "Married" && formData.marriageDate ? [{ label: "Marriage Date", value: formData.marriageDate }] : []),
          { label: "Joining with Family", value: formData.joiningWithFamily === "Yes" ? `Yes (${formData.familyMembers.length} members)` : "No" },
        ]}
        styles={styles}
        ReviewItem={ReviewItem}
      />

      <ReviewSection 
        title="Church Location"
        items={[
          { label: "Region", value: formData.region },
          { label: "Parish", value: formData.parish },
        ]}
        styles={styles}
        ReviewItem={ReviewItem}
      />

      <ReviewSection 
        title="Residential Address"
        items={[
          { label: "Continent", value: formData.addressContinent },
          { label: "Country", value: formData.addressCountry },
          { label: "Province", value: formData.addressProvince },
          { label: "District", value: formData.addressDistrict },
          { label: "Sector", value: formData.addressSector },
          { label: "Cell", value: formData.addressCell },
          { label: "Village", value: formData.addressVillage },
          { label: "Street", value: formData.street },
          ...(formData.postalCode ? [{ label: "Postal Code", value: formData.postalCode }] : []),
        ]}
        styles={styles}
        ReviewItem={ReviewItem}
      />

      <ReviewSection 
        title="Church Information"
        items={[
          { label: "Baptized", value: formData.isBaptized },
          ...(formData.isBaptized === "Yes" ? [{ label: "Baptism Date", value: formData.baptismDate }] : []),
          { label: "Interested Ministries", value: formData.interestedMinistries.length > 0 ? formData.interestedMinistries.join(", ") : "None selected" },
        ]}
        styles={styles}
        ReviewItem={ReviewItem}
      />

      {formData.joiningWithFamily === "Yes" && formData.familyMembers.length > 0 && (
        <div className={styles["review-section"]}>
          <h3 className={styles["review-section-title"]}>
            Family Members Joining ({formData.familyMembers.length})
          </h3>
          <div className={styles["family-members-review"]}>
            {formData.familyMembers.map((member, index) => {
              const memberAge = calculateAge(member.dateOfBirth);
              const requiresId = memberAge >= 16;
              return (
                <div key={member.id} className={styles["family-member-review"]}>
                  <div className={styles["family-member-review-header"]}>
                    <span className={styles["member-number"]}>{index + 1}.</span>
                    {member.firstName} {member.lastName}
                    {requiresId && member.idCard && (
                      <span className={styles["review-id-verified"]}>🆔</span>
                    )}
                  </div>
                  <div className={styles["family-member-review-details"]}>
                    <span>{member.relationship}</span>
                    <span>{member.gender}</span>
                    <span>Age {memberAge}</span>
                    {requiresId && (
                      <span className={member.idCard ? styles["id-provided"] : styles["id-missing"]}>
                        {member.idCard ? "ID Provided" : "ID Required"}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  </div>
);

// FamilyMembersModal Component
const FamilyMembersModal = ({ isOpen, onClose, familyMembers, currentMember, onMemberChange, onAddMember, onRemoveMember, calculateAge, styles }) => {
  if (!isOpen) return null;

  const memberAge = calculateAge(currentMember.dateOfBirth);
  const requiresId = memberAge >= 16;

  return (
    <div className={styles["modal-overlay"]}>
      <div className={styles["modal-content"]}>
        <div className={styles["modal-header"]}>
          <div className={styles["modal-title"]}>
            <h3>Add Family Member</h3>
          </div>
          <button onClick={onClose} className={styles["close-button"]}>×</button>
        </div>

        <div className={styles["modal-body"]}>
          <div className={styles["form-row"]}>
            <div className={styles["form-col"]}>
              <InputField
                label="First Name"
                placeholder="Enter first name"
                value={currentMember.firstName}
                onChange={(e) => onMemberChange({ ...currentMember, firstName: e.target.value })}
                required
              />
            </div>
            <div className={styles["form-col"]}>
              <InputField
                label="Last Name"
                placeholder="Enter last name"
                value={currentMember.lastName}
                onChange={(e) => onMemberChange({ ...currentMember, lastName: e.target.value })}
                required
              />
            </div>
          </div>

          <SearchSelect
            label="Relationship"
            value={currentMember.relationship}
            onChange={(value) => onMemberChange({ ...currentMember, relationship: value })}
            options={RELATIONSHIP_OPTIONS}
            placeholder="-- Select Relationship --"
            required
          />

          <InputField
            type="date"
            label="Date of Birth"
            value={currentMember.dateOfBirth}
            onChange={(e) => onMemberChange({ ...currentMember, dateOfBirth: e.target.value })}
            required
          />

          {currentMember.dateOfBirth && (
            <div className={`${styles["age-display"]} ${requiresId ? styles["id-required"] : ""}`}>
              <span>Age: {memberAge} years old</span>
              {requiresId && <span className={styles["id-badge"]}> • ID Card Required</span>}
            </div>
          )}

          {requiresId && (
            <InputField
              label="Identification Card Number"
              placeholder="Enter ID card number"
              value={currentMember.idCard}
              onChange={(e) => onMemberChange({ ...currentMember, idCard: e.target.value })}
              required
            />
          )}

          <RadioButton
            label="Gender"
            options={["Male", "Female"]}
            value={currentMember.gender}
            onChange={(value) => onMemberChange({ ...currentMember, gender: value })}
            required
          />
        </div>

        <div className={styles["modal-actions"]}>
          <button 
            className={styles["cancel-button"]}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button 
            className={styles["add-button"]}
            onClick={onAddMember}
            type="button"
            disabled={!currentMember.firstName || !currentMember.lastName || !currentMember.relationship || !currentMember.dateOfBirth || !currentMember.gender || (requiresId && !currentMember.idCard)}
          >
            <span className={styles["button-icon"]}>+</span>
            Add Member
          </button>
        </div>

        {familyMembers.length > 0 && (
          <div className={styles["family-members-preview"]}>
            <h4>Family Members Added ({familyMembers.length})</h4>
            <div className={styles["family-preview-list"]}>
              {familyMembers.map(member => {
                const previewAge = calculateAge(member.dateOfBirth);
                const previewRequiresId = previewAge >= 16;
                return (
                  <div key={member.id} className={styles["family-preview-item"]}>
                    <div className={styles["preview-info"]}>
                      <span>{member.firstName} {member.lastName}</span>
                      <span className={styles["preview-details"]}>
                        ({member.relationship}, Age {previewAge})
                        {previewRequiresId && (
                          <span className={member.idCard ? styles["preview-id-ok"] : styles["preview-id-missing"]}>
                            {member.idCard ? " 🆔" : " ⚠️"}
                          </span>
                        )}
                      </span>
                    </div>
                    <button
                      onClick={() => onRemoveMember(member.id)}
                      className={styles["remove-preview-button"]}
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ReviewItem Component
const ReviewItem = ({ label, value }) => (
  <div className={styles["review-item"]}>
    <span className={styles["review-label"]}>{label}:</span>
    <span className={styles["review-value"]}>{value || "Not provided"}</span>
  </div>
);

// Main MembershipForm Component
function MembershipForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [currentFamilyMember, setCurrentFamilyMember] = useState(INITIAL_FAMILY_MEMBER);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Select a random bible verse
  const [welcomeVerse] = useState(() => 
    BIBLE_VERSES[Math.floor(Math.random() * BIBLE_VERSES.length)]
  );

  const calculateAge = useCallback((dob) => {
    if (!dob) return 0;
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }, []);

  const currentAge = calculateAge(formData.dateOfBirth);
  const requiresIdCard = currentAge >= 16;

  const getLocationOptions = useCallback(() => {
    if (!formData.addressContinent) return { countries: [] };
    
    let currentLevel = LOCATION_DATA[formData.addressContinent];
    return {
      countries: Object.keys(currentLevel || {}),
      provinces: formData.addressCountry ? Object.keys(currentLevel[formData.addressCountry] || {}) : [],
      districts: formData.addressProvince ? Object.keys(currentLevel[formData.addressCountry][formData.addressProvince] || {}) : [],
      sectors: formData.addressDistrict ? Object.keys(currentLevel[formData.addressCountry][formData.addressProvince][formData.addressDistrict] || {}) : [],
      cells: formData.addressSector ? Object.keys(currentLevel[formData.addressCountry][formData.addressProvince][formData.addressDistrict][formData.addressSector] || {}) : [],
      villages: formData.addressCell ? (currentLevel[formData.addressCountry][formData.addressProvince][formData.addressDistrict][formData.addressSector][formData.addressCell] || []) : [],
    };
  }, [formData.addressContinent, formData.addressCountry, formData.addressProvince, formData.addressDistrict, formData.addressSector, formData.addressCell]);

  const locationOptions = getLocationOptions();

  const updateFormData = useCallback((updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  const handleJoiningWithFamilyChange = useCallback((value) => {
    updateFormData({ 
      joiningWithFamily: value,
      ...(value === "No" && { familyMembers: [] })
    });
    if (value === "Yes") {
      setShowFamilyModal(true);
    }
  }, [updateFormData]);

  const handleAddFamilyMember = useCallback(() => {
    if (!currentFamilyMember.firstName?.trim() || 
        !currentFamilyMember.lastName?.trim() || 
        !currentFamilyMember.relationship || 
        !currentFamilyMember.dateOfBirth || 
        !currentFamilyMember.gender) {
      alert("Please fill in all family member details");
      return;
    }
    
    const memberAge = calculateAge(currentFamilyMember.dateOfBirth);
    if (memberAge < 0) {
      alert("Please enter a valid date of birth");
      return;
    }

    if (memberAge >= 16 && !currentFamilyMember.idCard?.trim()) {
      alert("ID card is required for family members 16 years and above");
      return;
    }
    
    updateFormData({
      familyMembers: [...formData.familyMembers, { ...currentFamilyMember, id: Date.now() }]
    });
    setCurrentFamilyMember(INITIAL_FAMILY_MEMBER);
  }, [currentFamilyMember, formData.familyMembers, updateFormData, calculateAge]);

  const handleRemoveFamilyMember = useCallback((id) => {
    updateFormData({
      familyMembers: formData.familyMembers.filter(member => member.id !== id)
    });
  }, [formData.familyMembers, updateFormData]);

  const handleMinistryChange = useCallback((ministry) => {
    updateFormData({
      interestedMinistries: formData.interestedMinistries.includes(ministry) 
        ? formData.interestedMinistries.filter(m => m !== ministry)
        : [...formData.interestedMinistries, ministry]
    });
  }, [formData.interestedMinistries, updateFormData]);

  const handleBaptismChange = useCallback((value) => {
    updateFormData({ 
      isBaptized: value,
      ...(value === "No" && { baptismDate: "" })
    });
  }, [updateFormData]);

  const validateStep = useCallback((step) => {
    const newErrors = {};
    
    switch (step) {
      case 1:
        if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
        if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
        if (!formData.email.trim()) newErrors.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Please enter a valid email address";
        if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
        if (!formData.gender) newErrors.gender = "Gender is required";
        if (!formData.dateOfBirth) newErrors.dateOfBirth = "Date of birth is required";
        if (!formData.maritalStatus) newErrors.maritalStatus = "Marital status is required";
        if (requiresIdCard && !formData.idCard.trim()) newErrors.idCard = "ID card is required for applicants 16 years and above";
        
        // Validate family members if applicable
        if (formData.joiningWithFamily === "Yes") {
          formData.familyMembers.forEach((member, index) => {
            const memberAge = calculateAge(member.dateOfBirth);
            if (memberAge >= 16 && !member.idCard.trim()) {
              newErrors[`familyMember_${index}`] = `ID card required for ${member.firstName} ${member.lastName} (16+ years)`;
            }
          });
        }
        break;
      case 2:
        if (!formData.region) newErrors.region = "Region is required";
        if (!formData.parish) newErrors.parish = "Parish is required";
        break;
      case 3:
        if (!formData.addressContinent) newErrors.addressContinent = "Continent is required";
        if (!formData.addressCountry) newErrors.addressCountry = "Country is required";
        if (!formData.addressProvince) newErrors.addressProvince = "Province is required";
        if (!formData.addressDistrict) newErrors.addressDistrict = "District is required";
        if (!formData.addressSector) newErrors.addressSector = "Sector is required";
        if (!formData.addressCell) newErrors.addressCell = "Cell is required";
        if (!formData.addressVillage) newErrors.addressVillage = "Village is required";
        if (!formData.street.trim()) newErrors.street = "Street address is required";
        break;
      case 4:
        if (!formData.isBaptized) newErrors.isBaptized = "Please indicate if you are baptized";
        if (formData.isBaptized === "Yes" && !formData.baptismDate) newErrors.baptismDate = "Baptism date is required";
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, requiresIdCard, calculateAge]);

  const handleNext = useCallback(() => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep, validateStep]);

  const handleBack = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleStepClick = useCallback((step) => {
    if (step < currentStep) {
      setCurrentStep(step);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  const handleSubmit = useCallback(async () => {
    if (!validateStep(5)) return;
    
    setIsSubmitting(true);
    
    // Simulate API call
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const applicationData = {
        ...formData,
        age: currentAge,
        address: {
          continent: formData.addressContinent,
          country: formData.addressCountry,
          province: formData.addressProvince,
          district: formData.addressDistrict,
          sector: formData.addressSector,
          cell: formData.addressCell,
          village: formData.addressVillage,
          street: formData.street,
          postalCode: formData.postalCode,
        },
        idCard: requiresIdCard ? formData.idCard : null,
        baptismDate: formData.isBaptized === "Yes" ? formData.baptismDate : null,
        status: "pending",
        submittedDate: new Date().toISOString().split('T')[0],
      };
      
      console.log("Application Data:", applicationData);
      setShowSuccess(true);
    } catch (error) {
      console.error("Submission error:", error);
      alert("There was an error submitting your application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, currentAge, requiresIdCard, validateStep]);

  const handleClosePopup = useCallback(() => {
    setShowSuccess(false);
    setCurrentStep(1);
    setFormData(INITIAL_FORM_DATA);
    setCurrentFamilyMember(INITIAL_FAMILY_MEMBER);
    setErrors({});
    setIsSubmitting(false);
  }, []);

  return (
    <>
      <div className={styles["membership-container"]}>
        <div className={styles["membership-card"]}>
          <div className={styles["logo-container"]}>
            <img src={Logo} alt="Calvary Wide Fellowship Ministries" className={styles["church-logo"]} />
            
            {/* Form Header Section */}
            <div className={styles["form-header"]}>
              <h1 className={styles["form-title"]}>
                Membership Application
              </h1>
              <p className={styles["form-subtitle"]}>
                Join our church family and become part of our growing community
              </p>
            </div>

            <div className={styles["welcome-verse"]}>
              {welcomeVerse}
            </div>
          </div>
          
          <CustomProgressIndicator 
            currentStep={currentStep} 
            totalSteps={5}
            onStepClick={handleStepClick}
          />

          <div className={styles["step-header"]}>
            <h2 className={styles["step-title"]}>
              {STEP_CONFIG[currentStep].title}
            </h2>
            <p className={styles["step-subtitle"]}>
              {STEP_CONFIG[currentStep].subtitle}
            </p>
          </div>

          <div className={styles["membership-form"]}>
            {currentStep === 1 && (
              <PersonalInfoStep
                formData={formData}
                updateFormData={updateFormData}
                errors={errors}
                currentAge={currentAge}
                requiresIdCard={requiresIdCard}
                onJoiningWithFamilyChange={handleJoiningWithFamilyChange}
                onShowFamilyModal={() => setShowFamilyModal(true)}
                onRemoveFamilyMember={handleRemoveFamilyMember}
                calculateAge={calculateAge}
                styles={styles}
              />
            )}

            {currentStep === 2 && (
              <ChurchLocationStep
                formData={formData}
                updateFormData={updateFormData}
                errors={errors}
                styles={styles}
              />
            )}

            {currentStep === 3 && (
              <AddressStep
                formData={formData}
                updateFormData={updateFormData}
                errors={errors}
                locationOptions={locationOptions}
                styles={styles}
              />
            )}

            {currentStep === 4 && (
              <ChurchInfoStep
                formData={formData}
                updateFormData={updateFormData}
                errors={errors}
                onBaptismChange={handleBaptismChange}
                onMinistryChange={handleMinistryChange}
                styles={styles}
              />
            )}

            {currentStep === 5 && (
              <ReviewStep
                formData={formData}
                currentAge={currentAge}
                requiresIdCard={requiresIdCard}
                calculateAge={calculateAge}
                styles={styles}
                ReviewItem={ReviewItem}
              />
            )}

            <div className={styles["button-group"]}>
              {currentStep > 1 && (
                <Button 
                  label="← Back"
                  onClick={handleBack}
                  variant="secondary"
                  type="button"
                  disabled={isSubmitting}
                />
              )}
              {currentStep < 5 ? (
                <Button 
                  label="Continue →" 
                  onClick={handleNext}
                  variant="primary"
                  type="button"
                  disabled={isSubmitting}
                />
              ) : (
                <Button 
                  label={isSubmitting ? "Submitting..." : "Submit Application"} 
                  onClick={handleSubmit}
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <SuccessPopup
        show={showSuccess}
        onClose={handleClosePopup}
        title="Application Submitted Successfully!"
        message="Thank you for applying to join Calvary Wide Fellowship Ministries. Your application has been submitted and is pending approval from the church administrator. You will be notified via email once your application is reviewed."
        buttonText="Close"
      />

      <FamilyMembersModal
        isOpen={showFamilyModal}
        onClose={() => setShowFamilyModal(false)}
        familyMembers={formData.familyMembers}
        currentMember={currentFamilyMember}
        onMemberChange={setCurrentFamilyMember}
        onAddMember={handleAddFamilyMember}
        onRemoveMember={handleRemoveFamilyMember}
        calculateAge={calculateAge}
        styles={styles}
      />
    </>
  );
}

export default MembershipForm;